import {Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, Input, OnInit, signal} from '@angular/core';
import {ConfigService, FeatureTogglesService} from '@dcp/configurator';
import {LuigiContainer} from '@luigi-project/container';
import {GoogleAutocompleteService} from '@app/services/google-autocomplete.service';
import {LeadFormLuigiContext} from '@app/product/models/lead.model';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';

/**
 * Minimal host for the deployed `lead-form` web component. Mounts the
 * `#dcp-lead-form-modal` luigi-container so the support widget's `openEddlModal()`
 * has a live container to dispatch `dcp-lead-form:modal:open:*` into. On the PDP
 * nothing else mounts it, which is why "Ask Publisher" opened nothing (DCP-59441).
 */
@UntilDestroy()
@Component({
  selector: 'dcp-lead-form-modal',
  standalone: true,
  templateUrl: './dcp-lead-form-modal.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DcpLeadFormModalComponent implements OnInit {
  public siteKey: string;
  public url: string;
  public modalId: string = 'modal-lead-form';
  readonly gRecaptchaId: string = 'g-recaptcha-lead-form';

  public container: LuigiContainer;
  private _context: LeadFormLuigiContext;

  public leadFormWebComponentCaptchaFeatureToggle = signal<boolean>(false);
  private configService = inject(ConfigService);
  private autocompleteService = inject(GoogleAutocompleteService);
  private togglesService = inject(FeatureTogglesService);
  private elementRef = inject(ElementRef);

  @Input() set context(luigiContext: LeadFormLuigiContext) {
    this._context = {...this._context, ...luigiContext};
    this.container?.updateContext?.(this._context);
  }

  get context(): LeadFormLuigiContext {
    return this._context;
  }

  constructor() {
    this.url = this.configService.getString('webComponents.leadFormModal');
    this.siteKey = this.configService.getString('RE_CAPTCHA_SITE_KEY');
  }

  ngOnInit(): void {
    this.container = this.elementRef.nativeElement.querySelector('#dcp-lead-form-modal');

    this.container?.addEventListener('initialized', () => {
      this.autocompleteService.isLoaded().then(() => {
        this._context = {
          ...this._context,
          maps: window.google.maps,
          gRecaptchaSiteKey: this.siteKey,
          gRecaptchaId: this.gRecaptchaId,
        };
        this.container.updateContext(this._context);
        this.modalId = this._context?.modalId ?? this.modalId;
      });
    });

    this.togglesService
      .isEnabled(UNLEASH_TOGGLES.leadFormCaptcha)
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.leadFormWebComponentCaptchaFeatureToggle.set(isEnabled);
      });
  }
}
